import { useState, useCallback, useRef } from 'react';
import { Space, Typography, Input, message } from 'antd';
import { AppModal } from '@/components/ui/AppModal';
import { Button } from '@components/ui/Button';

/**
 * Two-step flow: (1) Yes/No to change status (2) required reason when deactivating.
 * Resolves { ok: true, reason } or { ok: false }.
 */
export function useDirectoryStatusChangeFlow() {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [displayName, setDisplayName] = useState('');
    const [reason, setReason] = useState('');
    const ctxRef = useRef({ nextActive: false });
    const resolveRef = useRef(null);

    const close = useCallback((result) => {
        setOpen(false);
        setStep(1);
        setReason('');
        setDisplayName('');
        resolveRef.current?.(result);
        resolveRef.current = null;
    }, []);

    const requestStatusChange = useCallback(({ displayName: name, nextActive }) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            ctxRef.current = { nextActive };
            setDisplayName(name || 'this record');
            setReason('');
            setStep(1);
            setOpen(true);
        });
    }, []);

    const handleConfirmYes = useCallback(() => {
        if (ctxRef.current.nextActive) {
            close({ ok: true, reason: '' });
        } else {
            setStep(2);
        }
    }, [close]);

    const handleConfirmNo = useCallback(() => {
        close({ ok: false });
    }, [close]);

    const handleSubmitReason = useCallback(() => {
        const r = reason.trim();
        if (!r) {
            message.warning('Please enter a reason');
            return;
        }
        close({ ok: true, reason: r });
    }, [reason, close]);

    const StatusChangeModal = (
        <AppModal
            open={open}
            title={step === 1 ? 'Confirm status change' : 'Reason for deactivation'}
            onCancel={() => close({ ok: false })}
            footer={null}
            destroyOnClose
            size="md"
        >
            {step === 1 ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Typography.Text>
                        {ctxRef.current.nextActive
                            ? `Turn on (activate) ${displayName}?`
                            : `Turn off (deactivate) ${displayName}?`}
                    </Typography.Text>
                    <Space>
                        <Button $primary={false} htmlType="button" onClick={handleConfirmNo}>
                            No
                        </Button>
                        <Button htmlType="button" onClick={handleConfirmYes}>
                            Yes
                        </Button>
                    </Space>
                </Space>
            ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Typography.Text type="secondary">
                        A reason is required before deactivating.
                    </Typography.Text>
                    <Input.TextArea
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason…"
                    />
                    <Space>
                        <Button $primary={false} htmlType="button" onClick={() => setStep(1)}>
                            Back
                        </Button>
                        <Button $primary={false} htmlType="button" onClick={() => close({ ok: false })}>
                            Cancel
                        </Button>
                        <Button htmlType="button" onClick={handleSubmitReason}>
                            Confirm
                        </Button>
                    </Space>
                </Space>
            )}
        </AppModal>
    );

    return { requestStatusChange, StatusChangeModal };
}
